/**
 * Один пакет `/history/:id` (§8, §11.7).
 *
 * Это страница, на которую ведут ссылки из Telegram (`/changes/:id`) и из
 * плашки «Данные обновлены» (§11.7): её открывают в новой вкладке, поэтому она
 * самодостаточна — заголовок, автор, комментарий, статус доставки, полный дифф
 * и откат. Центральная колонка 760 px, как на ревью.
 */
import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import type { ChangeSetDetail } from '@shared/types.ts'
import {
  Avatar, Badge, Button, ConsequenceLine, EmptyState, IconArrowLeft, Modal, Skeleton,
} from '../../components/index.js'
import { api, ApiRequestError } from '../../lib/api.js'
import { useApp } from '../../store/app-store.js'
import { ChangeDiff } from './ChangeDiff.js'
import { channelLabelOfChanges } from './ChangeSetRow.js'
import { countOverwritten } from './revert-impact.js'
import { formatRelative, historyTexts } from './texts.history.js'
import './ChangeSetScreen.css'

type LoadState = 'loading' | 'ready' | 'missing' | 'error'

export function ChangeSetScreen() {
  const { id = '' } = useParams()
  const numericId = Number(id)
  const valid = Number.isInteger(numericId) && numericId > 0

  const bootstrap = useApp((s) => s.bootstrap)
  const brandsById = useApp((s) => s.brandsById)
  const toast = useApp((s) => s.toast)
  const reloadBrands = useApp((s) => s.reloadBrands)

  const permissions = bootstrap?.user.permissions ?? []
  const canRevert = permissions.includes('changesets.revert')
  const canNotify = permissions.includes('settings.manage')
  const users = bootstrap?.users ?? []

  const [detail, setDetail] = useState<ChangeSetDetail | null>(null)
  const [state, setState] = useState<LoadState>('loading')
  const [error, setError] = useState<string | null>(null)
  const [reloadToken, setReloadToken] = useState(0)

  const [revertOpen, setRevertOpen] = useState(false)
  const [reverting, setReverting] = useState(false)
  const [notifying, setNotifying] = useState(false)

  useEffect(() => {
    if (!valid) {
      setState('missing')
      return
    }
    let alive = true
    setState('loading')
    setError(null)
    api.changeSet(numericId)
      .then((res) => {
        if (!alive) return
        setDetail(res)
        setState('ready')
      })
      .catch((err: unknown) => {
        if (!alive) return
        if (err instanceof ApiRequestError && err.code === 'not_found') {
          setState('missing')
          return
        }
        setError(err instanceof Error ? err.message : historyTexts.errorTitle)
        setState('error')
      })
    return () => { alive = false }
  }, [numericId, valid, reloadToken])

  const confirmRevert = useCallback(async () => {
    if (!detail) return
    setReverting(true)
    try {
      const res = await api.revert(detail.id)
      toast('success', historyTexts.revert.done(res.changeSetId, detail.id))
      setRevertOpen(false)
      setReloadToken((n) => n + 1)
      await reloadBrands()
    } catch (err: unknown) {
      toast('error', err instanceof ApiRequestError ? err.message : historyTexts.errorTitle)
      setRevertOpen(false)
    } finally {
      setReverting(false)
    }
  }, [detail, toast, reloadBrands])

  const notifyAgain = useCallback(async () => {
    if (!detail) return
    setNotifying(true)
    try {
      await api.notifyAgain(detail.id)
      toast('success', historyTexts.telegram.sentToast)
      setReloadToken((n) => n + 1)
    } catch (err: unknown) {
      toast('error', err instanceof ApiRequestError ? err.message : historyTexts.errorTitle)
    } finally {
      setNotifying(false)
    }
  }, [detail, toast])

  const back = (
    <Link className="packscreen__back t-cell" to="/history">
      <IconArrowLeft />
      {historyTexts.pack.back}
    </Link>
  )

  if (state === 'missing') {
    return (
      <div className="packscreen">
        {back}
        <EmptyState
          title={historyTexts.pack.notFoundTitle}
          text={historyTexts.pack.notFoundText}
          actions={<Link className="packscreen__cta" to="/history">{historyTexts.pack.toHistory}</Link>}
        />
      </div>
    )
  }

  if (state === 'error') {
    return (
      <div className="packscreen">
        {back}
        <EmptyState
          title={historyTexts.errorTitle}
          text={error}
          actions={<Button variant="primary" onClick={() => setReloadToken((n) => n + 1)}>{historyTexts.retry}</Button>}
        />
      </div>
    )
  }

  if (state === 'loading' || !detail) {
    return (
      <div className="packscreen">
        {back}
        <h1 className="t-h1">{historyTexts.pack.title(id)}</h1>
        <Skeleton rows={6} columns={[220, 120, 120]} label={historyTexts.loadingLabel} />
      </div>
    )
  }

  const author = users.find((user) => user.id === detail.authorId)
  const authorName = detail.isSystem
    ? historyTexts.systemAuthor
    : author?.shortName ?? historyTexts.unknownAuthor
  const channel = channelLabelOfChanges(detail)

  const revertBlockedReason = detail.isSystem
    ? historyTexts.revert.systemDisabled
    : detail.status === 'reverted'
      ? historyTexts.revert.alreadyReverted
      : !canRevert
        ? historyTexts.revert.noRight
        : null

  const overwritten = countOverwritten(detail, brandsById)

  return (
    <div className="packscreen">
      {back}

      <header className="packscreen__head">
        <div className="packscreen__title">
          <h1 className="t-h1">{historyTexts.pack.title(detail.id)}</h1>
          <div className="packscreen__marks">
            {detail.isSystem ? <Badge variant="neutral">{historyTexts.marks.system}</Badge> : null}
            {detail.revertsChangeSetId !== null ? (
              <Link to={`/history/${detail.revertsChangeSetId}`} className="packscreen__mark-link">
                <Badge variant="accent">{historyTexts.marks.revertOf(detail.revertsChangeSetId)}</Badge>
              </Link>
            ) : null}
            {detail.revertedByChangeSetId !== null ? (
              <Link to={`/history/${detail.revertedByChangeSetId}`} className="packscreen__mark-link">
                <Badge variant="neutral">{historyTexts.marks.revertedBy(detail.revertedByChangeSetId)}</Badge>
              </Link>
            ) : null}
          </div>
        </div>

        <p className="packscreen__meta t-meta">
          <Avatar name={authorName} system={detail.isSystem} />
          {historyTexts.pack.subtitle(
            authorName,
            formatRelative(detail.committedAt),
            detail.changes.length,
            channel,
          )}
        </p>
      </header>

      <section className="packscreen__note" aria-label={historyTexts.pack.noteLabel}>
        <span className="packscreen__note-label t-label">{historyTexts.pack.noteLabel}</span>
        <p className={detail.note ? 'packscreen__note-body t-body' : 'packscreen__note-body packscreen__note-body--empty t-body'}>
          {detail.note ?? historyTexts.pack.noteEmpty}
        </p>
      </section>

      <section className="packscreen__telegram" aria-label={historyTexts.columns.telegram}>
        <span className="packscreen__note-label t-label">{historyTexts.columns.telegram}</span>
        <div className="packscreen__telegram-row">
          {detail.isSystem ? (
            <span className="t-cell packscreen__muted">{historyTexts.telegram.skipped}</span>
          ) : detail.telegramStatus === 'sent' ? (
            <Badge variant="success">{historyTexts.telegram.sent}</Badge>
          ) : detail.telegramStatus === 'pending' ? (
            <Badge variant="neutral" dot>{historyTexts.telegram.pending}</Badge>
          ) : detail.telegramStatus === 'failed' ? (
            <Badge variant="danger">{historyTexts.telegram.failed}</Badge>
          ) : (
            <span className="t-cell packscreen__muted">{historyTexts.telegram.skipped}</span>
          )}

          {detail.telegramStatus === 'failed' ? (
            canNotify ? (
              <Button variant="secondary" size="s" loading={notifying} onClick={() => void notifyAgain()}>
                {historyTexts.telegram.retry}
              </Button>
            ) : (
              <span className="t-meta packscreen__muted">{historyTexts.telegram.noRight}</span>
            )
          ) : null}
        </div>
      </section>

      <section className="packscreen__diff" aria-label={historyTexts.pack.title(detail.id)}>
        <ChangeDiff changes={detail.changes} />
      </section>

      <footer className="packscreen__foot">
        <Button
          variant="danger-ghost"
          disabled={revertBlockedReason !== null}
          aria-describedby={revertBlockedReason ? 'pack-revert-reason' : undefined}
          onClick={() => setRevertOpen(true)}
        >
          {historyTexts.revert.action}
        </Button>
        {revertBlockedReason ? (
          <ConsequenceLine id="pack-revert-reason">{revertBlockedReason}</ConsequenceLine>
        ) : null}
      </footer>

      <Modal
        open={revertOpen}
        onClose={() => setRevertOpen(false)}
        title={historyTexts.revert.title(detail.id)}
        size="s"
        footerNote={<ConsequenceLine tone="danger">{historyTexts.revert.consequence}</ConsequenceLine>}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRevertOpen(false)}>
              {historyTexts.revert.cancel}
            </Button>
            <Button variant="primary" loading={reverting} onClick={() => void confirmRevert()}>
              {historyTexts.revert.confirm}
            </Button>
          </>
        }
        onConfirm={() => void confirmRevert()}
      >
        <p className="t-body">
          {historyTexts.revert.counts(detail.id, detail.changes.length)}
          {overwritten > 0 ? ` ${historyTexts.revert.overwritten(overwritten)}` : ''}
        </p>
      </Modal>
    </div>
  )
}
